import { localizedGymContent, type GymContentTranslations } from '@atomfit/shared';
import { GymPhotoGallery } from '../gym-photos/GymPhotoGallery.js';
import { getLocale, translate } from '../../i18n/index.js';
import { useCallback, useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import {
  CarFront,
  Check,
  ChevronRight,
  Dumbbell,
  ExternalLink,
  Info,
  MapPin,
  TramFront,
  X,
} from 'lucide-react';

export interface GymInfo {
  id: string;
  name: string;
  addressLine1: string;
  addressLine2?: string | null;
  city: string;
  postcode?: string;
  lat?: number | null;
  lng?: number | null;
  contentTranslations?: GymContentTranslations;
  equipmentDescription?: string | null;
  directionsDescription?: string | null;
}

export function GymInfoDisclosure({ gym }: { gym: GymInfo }) {
  const [isOpen, setIsOpen] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const equipment = lines(
    localizedGymContent(
      gym.contentTranslations,
      'equipmentDescription',
      getLocale(),
      gym.equipmentDescription,
    ),
  );
  const directions = directionSections(
    localizedGymContent(
      gym.contentTranslations,
      'directionsDescription',
      getLocale(),
      gym.directionsDescription,
    ),
  );
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const closeTimerRef = useRef<number | null>(null);
  const titleId = useId();
  const modalRef = useRef<HTMLElement>(null);
  const mapQuery = gymMapQuery(gym);
  const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(mapQuery)}`;
  const embedUrl = `https://www.google.com/maps?q=${encodeURIComponent(mapQuery)}&z=16&output=embed`;

  const finishClose = useCallback(() => {
    if (closeTimerRef.current !== null) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
    setIsOpen(false);
    setIsClosing(false);
  }, []);

  const dismissModal = useCallback(() => {
    if (!isOpen || isClosing) return;

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      finishClose();
      return;
    }

    setIsClosing(true);
    closeTimerRef.current = window.setTimeout(finishClose, 220);
  }, [finishClose, isClosing, isOpen]);

  const openModal = () => {
    if (closeTimerRef.current !== null) window.clearTimeout(closeTimerRef.current);
    closeTimerRef.current = null;
    setIsClosing(false);
    setIsOpen(true);
  };

  useEffect(() => {
    if (!isOpen) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeButtonRef.current?.focus();

    return () => {
      document.body.style.overflow = previousOverflow;
      triggerRef.current?.focus();
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const closeOnEscape = (event: KeyboardEvent) => {
      if (document.querySelector('dialog[data-gym-photo-viewer][open]')) return;
      if (event.key === 'Escape' && !event.defaultPrevented) dismissModal();
      if (event.key === 'Tab') {
        const focusable = Array.from(
          modalRef.current?.querySelectorAll<HTMLElement>(
            'button:not(:disabled), a[href], input:not(:disabled), [tabindex="0"]',
          ) ?? [],
        ).filter((el) => el.getClientRects().length > 0);
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last?.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first?.focus();
        }
      }
    };
    window.addEventListener('keydown', closeOnEscape);
    return () => window.removeEventListener('keydown', closeOnEscape);
  }, [dismissModal, isOpen]);

  useEffect(
    () => () => {
      if (closeTimerRef.current !== null) window.clearTimeout(closeTimerRef.current);
    },
    [],
  );

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className="af-gym-info-trigger"
        onClick={openModal}
        aria-haspopup="dialog"
      >
        <span className="af-gym-info-summary-main">
          <span className="af-gym-info-summary-icon" aria-hidden="true">
            <Info size={16} strokeWidth={2.2} />
          </span>
          <span className="af-gym-info-summary-copy">
            <strong>
              {translate('components.gyms.gym_info_disclosure.gym_info_disclosure.strong.gym')}
            </strong>
          </span>
        </span>
        <ChevronRight className="af-gym-info-chevron" size={18} aria-hidden="true" />
      </button>

      {isOpen &&
        createPortal(
          <div
            className={`af-gym-info-modal-backdrop${isClosing ? ' is-closing' : ''}`}
            onMouseDown={(event) => {
              if (event.target === event.currentTarget) dismissModal();
            }}
            onAnimationEnd={(event) => {
              if (
                isClosing &&
                event.target === event.currentTarget &&
                event.animationName === 'af-gym-info-backdrop-out'
              ) {
                finishClose();
              }
            }}
          >
            <section
              className="af-gym-info-modal"
              ref={modalRef}
              role="dialog"
              aria-modal="true"
              aria-labelledby={titleId}
            >
              <header className="af-gym-info-modal-header">
                <div>
                  <span>
                    {translate('components.gyms.gym_info_disclosure.gym_info_disclosure.span.gym')}
                  </span>
                  <h2 id={titleId}>{gym.name}</h2>
                  <p>{gymAddress(gym)}</p>
                </div>
                <button
                  ref={closeButtonRef}
                  type="button"
                  onClick={dismissModal}
                  aria-label={translate(
                    'components.gyms.gym_info_disclosure.gym_info_disclosure.button.aria_label_gym_close',
                  )}
                >
                  <X size={21} aria-hidden="true" />
                </button>
              </header>

              <div className="af-gym-info-modal-body">
                <GymPhotoGallery gymId={gym.id} gymName={gym.name} />
                <div className="af-gym-info-content">
                  {equipment.length > 0 && (
                    <section className="af-gym-info-section af-gym-info-equipment">
                      <SectionHeading
                        icon={<Dumbbell size={18} strokeWidth={2.1} />}
                        title={translate(
                          'components.gyms.gym_info_disclosure.gym_info_disclosure.section_heading.gym_equipment',
                        )}
                        meta={translate('gymEditor.equipmentCount', { count: equipment.length })}
                      />

                      <ul className="af-gym-info-equipment-list">
                        {equipment.map((item, index) => (
                          <li key={`${item}-${index}`}>
                            <span aria-hidden="true">
                              <Check size={14} strokeWidth={2.6} />
                            </span>
                            {item}
                          </li>
                        ))}
                      </ul>
                    </section>
                  )}

                  <div className="af-gym-info-side">
                    {directions.length > 0 && (
                      <section className="af-gym-info-section af-gym-info-directions">
                        <SectionHeading
                          icon={<TramFront size={18} strokeWidth={2.1} />}
                          title={translate(
                            'components.gyms.gym_info_disclosure.gym_info_disclosure.section_heading.title',
                          )}
                        />

                        <div className="af-gym-info-directions-list">
                          {directions.map((direction, index) => {
                            const isCar = /aut|park/i.test(direction.title);
                            return (
                              <article key={`${direction.title}-${index}`}>
                                <span className="af-gym-info-direction-icon" aria-hidden="true">
                                  {isCar ? (
                                    <CarFront size={17} strokeWidth={2} />
                                  ) : (
                                    <TramFront size={17} strokeWidth={2} />
                                  )}
                                </span>
                                <div>
                                  <h4>{direction.title}</h4>
                                  <p>{direction.body}</p>
                                </div>
                              </article>
                            );
                          })}
                        </div>
                      </section>
                    )}

                    <section className="af-gym-info-section af-gym-info-map-section">
                      <div className="af-gym-info-map-heading">
                        <div className="af-gym-info-map-title">
                          <span aria-hidden="true">
                            <MapPin size={17} strokeWidth={2.2} />
                          </span>
                          <div>
                            <h3>
                              {translate(
                                'components.gyms.gym_info_disclosure.gym_info_disclosure.h3.map',
                              )}
                            </h3>
                            <p>{gymAddress(gym)}</p>
                          </div>
                        </div>
                        <a href={mapsUrl} target="_blank" rel="noreferrer">
                          {translate(
                            'components.gyms.gym_info_disclosure.gym_info_disclosure.a.open',
                          )}

                          <ExternalLink size={14} aria-hidden="true" />
                        </a>
                      </div>
                      <iframe
                        className="af-gym-info-map"
                        title={translate(
                          'components.gyms.gym_info_disclosure.gym_info_disclosure.iframe_gym_info_map.map',
                          { value1: gym.name },
                        )}
                        src={embedUrl}
                        loading="lazy"
                        referrerPolicy="no-referrer-when-downgrade"
                      />
                    </section>
                  </div>
                </div>
              </div>
            </section>
          </div>,
          document.body,
        )}
    </>
  );
}

function SectionHeading({ icon, title, meta }: { icon: ReactNode; title: string; meta?: string }) {
  return (
    <div className="af-gym-info-section-heading">
      <span aria-hidden="true">{icon}</span>
      <h3>{title}</h3>
      {meta && <small>{meta}</small>}
    </div>
  );
}

function lines(value: string | null | undefined) {
  return (value ?? '')
    .split(/\r?\n/)
    .map((line) => line.trim().replace(/^[-•]\s*/, ''))
    .filter(Boolean);
}

function directionSections(value: string | null | undefined) {
  return (value ?? '')
    .split(/\r?\n\s*\r?\n/)
    .map((block) =>
      block
        .split(/\r?\n/)
        .map((line) => line.trim())
        .filter(Boolean),
    )
    .filter((block) => block.length > 0)
    .map((block) => ({
      title:
        block.length > 1
          ? block[0]!
          : translate('components.gyms.gym_info_disclosure.direction_sections.directions'),
      body: block.length > 1 ? block.slice(1).join(' ') : block[0]!,
    }));
}

function gymAddress(gym: GymInfo) {
  return [gym.addressLine1, gym.addressLine2, [gym.postcode, gym.city].filter(Boolean).join(' ')]
    .filter(Boolean)
    .join(', ');
}

function gymMapQuery(gym: GymInfo) {
  const lat = Number(gym.lat);
  const lng = Number(gym.lng);
  if (Number.isFinite(lat) && Number.isFinite(lng) && gym.lat !== null && gym.lng !== null) {
    return `${lat},${lng}`;
  }
  return gymAddress(gym);
}
